/**
 * RTWO Wallet Engine — Domain Models
 * Phase 24 | Module ID: RTWO-WALT-001
 * Version: 1.0.0-phase24
 *
 * Strongly typed domain models for the RTWO Wallet Engine.
 * Defines the enterprise wallet operating layer for wallet lifecycle management,
 * account abstraction, transaction requests, balance services, address management,
 * wallet policies, signing requests, and asset management.
 *
 * Truthful Data Policy:
 * - No fake wallets, addresses, balances, assets, or transactions.
 * - Private keys are NEVER stored, displayed, or returned.
 * - All signing operations are delegated to the Cryptography Engine.
 * - All runtime values are null until real backend services are connected.
 *
 * Source: Wallet Engine (RTWO-WALT-001)
 */

// ─────────────────────────────────────────────
// PRIMITIVE ID TYPES
// ─────────────────────────────────────────────

export type WalletId = string;
export type AddressId = string;
export type AccountId = string;
export type AssetId = string;
export type RequestId = string;
export type PolicyId = string;
export type SigningRequestId = string;
export type LedgerAccountRef = string;
export type PublicKeyRef = string; // Reference only — never the actual key
export type WalletTimestamp = number | null;

export const WALLET_ENGINE_VERSION = {
  moduleId: 'RTWO-WALT-001',
  moduleName: 'Wallet Engine',
  version: '1.0.0-phaseB2',
  phase: 'Phase B.2 — Wallet Cryptography Runtime Activation',
  status: 'Cryptography Provider Connected — Wallet Provider Connected — Ledger and RPC Pending',
  productionReadiness:
    'Runtime Wallet-Cryptography Bridge Active — Balances and Ledger Transactions Pending',
  policy: 'Truthful Data Policy Enforced — No Fake Wallets, Balances, or Transactions',
} as const;

// ─────────────────────────────────────────────
// WALLET TYPE
// ─────────────────────────────────────────────

export type WalletType =
  | 'standard'
  | 'multisig'
  | 'enterprise'
  | 'validator'
  | 'governance'
  | 'system'
  | 'custodial'
  | 'non_custodial';

// ─────────────────────────────────────────────
// ADDRESS TYPE
// ─────────────────────────────────────────────

export type WalletAddressType =
  'primary' | 'change' | 'staking' | 'governance' | 'contract' | 'multisig';

// ─────────────────────────────────────────────
// ACCOUNT TYPE
// ─────────────────────────────────────────────

export type WalletAccountType = 'standard' | 'staking' | 'governance' | 'enterprise' | 'system';

// ─────────────────────────────────────────────
// ASSET TYPE
// ─────────────────────────────────────────────

export type AssetType =
  'native' | 'token' | 'nft' | 'governance_token' | 'staking_token' | 'system_asset';

// ─────────────────────────────────────────────
// BALANCE SOURCE
// ─────────────────────────────────────────────

export type BalanceSource = 'ledger_engine' | 'state_engine' | 'not_connected' | 'not_configured';

// ─────────────────────────────────────────────
// FEE POLICY
// ─────────────────────────────────────────────

export type FeePolicy = 'standard' | 'fast' | 'economy' | 'custom' | 'not_configured';

// ─────────────────────────────────────────────
// WALLET LIFECYCLE
// ─────────────────────────────────────────────

/**
 * Wallet Lifecycle:
 * Created → Configured → Activated → Locked → Unlocked → Suspended → Archived
 */
export type WalletStatus =
  'created' | 'configured' | 'activated' | 'locked' | 'unlocked' | 'suspended' | 'archived';

export interface WalletLifecycleStage {
  status: WalletStatus;
  label: string;
  description: string;
  terminal: boolean;
  requiresLedger: boolean;
  requiresCryptography: boolean;
  securityNote: string;
}

export const WALLET_LIFECYCLE: WalletLifecycleStage[] = [
  {
    status: 'created',
    label: 'Created',
    description:
      'Wallet record has been created. Identity and type assigned. Awaiting configuration.',
    terminal: false,
    requiresLedger: false,
    requiresCryptography: false,
    securityNote: 'Wallet exists as a record only. No keys or addresses assigned yet.',
  },
  {
    status: 'configured',
    label: 'Configured',
    description: 'Wallet has been configured with address, account, and policy settings.',
    terminal: false,
    requiresLedger: false,
    requiresCryptography: true,
    securityNote: 'Configuration requires Cryptography Engine for address assignment.',
  },
  {
    status: 'activated',
    label: 'Activated',
    description: 'Wallet is active and authorized for transaction requests and balance queries.',
    terminal: false,
    requiresLedger: true,
    requiresCryptography: true,
    securityNote: 'Activation requires Ledger connection for balance state.',
  },
  {
    status: 'locked',
    label: 'Locked',
    description: 'Wallet is temporarily locked. No transaction requests can be submitted.',
    terminal: false,
    requiresLedger: false,
    requiresCryptography: false,
    securityNote: 'Locked wallets cannot sign or submit transactions.',
  },
  {
    status: 'unlocked',
    label: 'Unlocked',
    description: 'Wallet has been unlocked and is authorized for operations.',
    terminal: false,
    requiresLedger: true,
    requiresCryptography: true,
    securityNote: 'Unlock requires authentication via Cryptography Engine.',
  },
  {
    status: 'suspended',
    label: 'Suspended',
    description: 'Wallet has been suspended by an operator or governance action.',
    terminal: false,
    requiresLedger: false,
    requiresCryptography: false,
    securityNote: 'Suspended wallets are frozen. All operations are blocked.',
  },
  {
    status: 'archived',
    label: 'Archived',
    description: 'Wallet has been archived. Retained for audit and compliance purposes.',
    terminal: true,
    requiresLedger: false,
    requiresCryptography: false,
    securityNote: 'Archived wallets are read-only. No operations permitted.',
  },
];

export const WALLET_STATUS_LABELS: Record<WalletStatus, string> = {
  created: 'Created',
  configured: 'Configured',
  activated: 'Activated',
  locked: 'Locked',
  unlocked: 'Unlocked',
  suspended: 'Suspended',
  archived: 'Archived',
};

export const WALLET_STATUS_COLORS: Record<WalletStatus, string> = {
  created: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
  configured: 'text-cyan-400 bg-cyan-400/10 border-cyan-400/20',
  activated: 'text-green-400 bg-green-400/10 border-green-400/20',
  locked: 'text-amber-400 bg-amber-400/10 border-amber-400/20',
  unlocked: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20',
  suspended: 'text-orange-400 bg-orange-400/10 border-orange-400/20',
  archived: 'text-slate-500 bg-slate-500/10 border-slate-500/20',
};

// ─────────────────────────────────────────────
// TRANSACTION REQUEST LIFECYCLE
// ─────────────────────────────────────────────

/**
 * Transaction Request Lifecycle:
 * Created → Validated → Pending Signature → Signed → Pending Ledger → Completed → Rejected → Cancelled
 */
export type TransactionRequestStatus =
  | 'created'
  | 'validated'
  | 'pending_signature'
  | 'signed'
  | 'pending_ledger'
  | 'completed'
  | 'rejected'
  | 'cancelled';

export interface TransactionRequestLifecycleStage {
  status: TransactionRequestStatus;
  label: string;
  description: string;
  terminal: boolean;
  requiresCryptography: boolean;
  requiresLedger: boolean;
}

export const TRANSACTION_REQUEST_LIFECYCLE: TransactionRequestLifecycleStage[] = [
  {
    status: 'created',
    label: 'Created',
    description: 'Transaction request has been created and queued for validation.',
    terminal: false,
    requiresCryptography: false,
    requiresLedger: false,
  },
  {
    status: 'validated',
    label: 'Validated',
    description: 'Transaction request has been validated against wallet policy and balance state.',
    terminal: false,
    requiresCryptography: false,
    requiresLedger: true,
  },
  {
    status: 'pending_signature',
    label: 'Pending Signature',
    description:
      'Transaction request is awaiting cryptographic signature from the Cryptography Engine.',
    terminal: false,
    requiresCryptography: true,
    requiresLedger: false,
  },
  {
    status: 'signed',
    label: 'Signed',
    description:
      'Transaction request has been signed by the Cryptography Engine. Ready for ledger submission.',
    terminal: false,
    requiresCryptography: true,
    requiresLedger: false,
  },
  {
    status: 'pending_ledger',
    label: 'Pending Ledger',
    description: 'Signed transaction is pending submission to the Ledger & State Engine.',
    terminal: false,
    requiresCryptography: false,
    requiresLedger: true,
  },
  {
    status: 'completed',
    label: 'Completed',
    description: 'Transaction has been accepted and finalized by the Ledger & State Engine.',
    terminal: true,
    requiresCryptography: false,
    requiresLedger: true,
  },
  {
    status: 'rejected',
    label: 'Rejected',
    description: 'Transaction was rejected by the Ledger Engine or wallet policy.',
    terminal: true,
    requiresCryptography: false,
    requiresLedger: false,
  },
  {
    status: 'cancelled',
    label: 'Cancelled',
    description: 'Transaction request was cancelled by the wallet owner or operator.',
    terminal: true,
    requiresCryptography: false,
    requiresLedger: false,
  },
];

export const TX_REQUEST_STATUS_LABELS: Record<TransactionRequestStatus, string> = {
  created: 'Created',
  validated: 'Validated',
  pending_signature: 'Pending Signature',
  signed: 'Signed',
  pending_ledger: 'Pending Ledger',
  completed: 'Completed',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
};

export const TX_REQUEST_STATUS_COLORS: Record<TransactionRequestStatus, string> = {
  created: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
  validated: 'text-cyan-400 bg-cyan-400/10 border-cyan-400/20',
  pending_signature: 'text-amber-400 bg-amber-400/10 border-amber-400/20',
  signed: 'text-indigo-400 bg-indigo-400/10 border-indigo-400/20',
  pending_ledger: 'text-purple-400 bg-purple-400/10 border-purple-400/20',
  completed: 'text-green-400 bg-green-400/10 border-green-400/20',
  rejected: 'text-red-400 bg-red-400/10 border-red-400/20',
  cancelled: 'text-slate-400 bg-slate-400/10 border-slate-400/20',
};

// ─────────────────────────────────────────────
// ADDRESS STATUS
// ─────────────────────────────────────────────

export type WalletAddressStatus = 'active' | 'inactive' | 'deprecated' | 'not_configured';

// ─────────────────────────────────────────────
// ACCOUNT STATUS
// ─────────────────────────────────────────────

export type WalletAccountStatus = 'active' | 'inactive' | 'suspended' | 'not_connected';

// ─────────────────────────────────────────────
// BALANCE STATUS
// ─────────────────────────────────────────────

export type BalanceStatus = 'available' | 'stale' | 'not_connected' | 'not_configured';

// ─────────────────────────────────────────────
// ASSET STATUS
// ─────────────────────────────────────────────

export type AssetStatus = 'registered' | 'active' | 'deprecated' | 'not_registered';

// ─────────────────────────────────────────────
// POLICY STATUS
// ─────────────────────────────────────────────

export type PolicyStatus = 'active' | 'inactive' | 'not_configured';

// ─────────────────────────────────────────────
// DOMAIN MODEL 1: RTWOWallet
// ─────────────────────────────────────────────

export interface RTWOWallet {
  walletId: WalletId;
  walletType: WalletType;
  ownerId: string;
  defaultAddress: string | null; // null until Cryptography Engine provides real address
  walletStatus: WalletStatus;
  createdAt: WalletTimestamp;
  updatedAt: WalletTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 2: RTWOWalletAddress
// ─────────────────────────────────────────────

export interface RTWOWalletAddress {
  addressId: AddressId;
  walletId: WalletId;
  address: string | null; // null until Cryptography Engine derives real address
  addressType: WalletAddressType;
  publicKeyReference: PublicKeyRef | null; // Reference only — NEVER the actual public key bytes
  status: WalletAddressStatus;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 3: RTWOWalletAccount
// ─────────────────────────────────────────────

export interface RTWOWalletAccount {
  accountId: AccountId;
  walletId: WalletId;
  ledgerAccountReference: LedgerAccountRef | null; // null until Ledger Engine is connected
  accountType: WalletAccountType;
  status: WalletAccountStatus;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 4: RTWOWalletBalance
// ─────────────────────────────────────────────

export interface RTWOWalletBalance {
  walletId: WalletId;
  assetId: AssetId;
  availableBalance: string | null; // null until Ledger Engine is connected
  lockedBalance: string | null;
  pendingBalance: string | null;
  balanceSource: BalanceSource;
  status: BalanceStatus;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 5: RTWOAsset
// ─────────────────────────────────────────────

export interface RTWOAsset {
  assetId: AssetId;
  symbol: string;
  name: string;
  decimals: number;
  assetType: AssetType;
  status: AssetStatus;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 6: RTWOTransactionRequest
// ─────────────────────────────────────────────

export interface RTWOTransactionRequest {
  requestId: RequestId;
  walletId: WalletId;
  fromAddress: string | null;
  toAddress: string | null;
  asset: AssetId | null;
  amount: string | null;
  feePolicy: FeePolicy;
  requestStatus: TransactionRequestStatus;
  createdAt: WalletTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 7: RTWOWalletPolicy
// ─────────────────────────────────────────────

export interface RTWOWalletPolicy {
  policyId: PolicyId;
  walletId: WalletId;
  securityPolicy: string | null;
  signingPolicy: string | null;
  backupPolicy: string | null;
  recoveryPolicy: string | null;
  status: PolicyStatus;
}

// ─────────────────────────────────────────────
// WALLET ENGINE EVENTS
// ─────────────────────────────────────────────

export type WalletEventType =
  | 'wallet_created'
  | 'wallet_configured'
  | 'wallet_activated'
  | 'wallet_locked'
  | 'wallet_unlocked'
  | 'wallet_suspended'
  | 'wallet_archived'
  | 'address_assigned'
  | 'account_linked'
  | 'balance_queried'
  | 'transaction_request_created'
  | 'transaction_request_validated'
  | 'signing_request_sent'
  | 'signing_request_completed'
  | 'transaction_submitted'
  | 'transaction_completed'
  | 'transaction_rejected'
  | 'policy_applied'
  | 'asset_registered'
  | 'wallet_provider_registered'
  | 'wallet_provider_connected'
  | 'configuration_bound'
  | 'repositories_bootstrapped';

export interface WalletEvent {
  eventId: string;
  eventType: WalletEventType;
  walletId: WalletId | null;
  timestamp: WalletTimestamp;
  payload: Record<string, unknown>;
}

// ─────────────────────────────────────────────
// WALLET ENGINE STATUS
// ─────────────────────────────────────────────

export type WalletEngineStatus =
  | 'not_initialized'
  | 'initializing'
  | 'structure_complete'
  | 'waiting_for_ledger'
  | 'waiting_for_cryptography'
  | 'operational'
  | 'error'
  | 'shutdown';

export interface WalletEngineState {
  status: WalletEngineStatus;
  moduleId: string;
  moduleName: string;
  version: string;
  phase: string;
  ledgerConnected: false;
  cryptographyConnected: false;
  walletCount: null;
  assetCount: null;
  source: 'Wallet Engine — Structure Complete';
}

export const WALLET_ENGINE_INITIAL_STATE: WalletEngineState = {
  status: 'structure_complete',
  moduleId: WALLET_ENGINE_VERSION.moduleId,
  moduleName: WALLET_ENGINE_VERSION.moduleName,
  version: WALLET_ENGINE_VERSION.version,
  phase: WALLET_ENGINE_VERSION.phase,
  ledgerConnected: false,
  cryptographyConnected: false,
  walletCount: null,
  assetCount: null,
  source: 'Wallet Engine — Structure Complete',
};

// ─────────────────────────────────────────────
// SERVICE IDs
// ─────────────────────────────────────────────

export const WALLET_SERVICE_IDS = {
  WALLET_MANAGEMENT: 'wallet-management-service',
  WALLET_ADDRESS: 'wallet-address-service',
  BALANCE: 'balance-service',
  TRANSACTION_REQUEST: 'transaction-request-service',
  ASSET: 'asset-service',
  WALLET_POLICY: 'wallet-policy-service',
  SIGNING_REQUEST: 'signing-request-service',
  WALLET_LIFECYCLE: 'wallet-lifecycle-service',
  WALLET_EVENT_PUBLISHER: 'wallet-event-publisher',
  WALLET_REGISTRY: 'wallet-registry-service',
  PROVIDER_LIFECYCLE: 'wallet-provider-lifecycle',
  PROVIDER_REGISTRATION: 'wallet-provider-registration',
  CONFIGURATION_BINDING: 'wallet-configuration-binding',
  REPOSITORY_BOOTSTRAP: 'wallet-repository-bootstrap',
} as const;

export type WalletRepositoryAccessStatus = 'not_configured' | 'read_only' | 'read_write';

export interface RTWOWalletRepositoryDescriptor {
  repositoryId: string;
  repositoryName: string;
  description: string;
  accessStatus: WalletRepositoryAccessStatus;
  recordCount: number | null;
  lastAccessedAt: WalletTimestamp;
}

export const WALLET_REPOSITORY_DEFINITIONS: RTWOWalletRepositoryDescriptor[] = [
  {
    repositoryId: 'repo-wallets',
    repositoryName: 'WalletRepository',
    description: 'Stores RTWOWallet records ingested from wallet provider.',
    accessStatus: 'not_configured',
    recordCount: null,
    lastAccessedAt: null,
  },
  {
    repositoryId: 'repo-addresses',
    repositoryName: 'WalletAddressRepository',
    description: 'Stores RTWOWalletAddress records. No fabricated addresses.',
    accessStatus: 'not_configured',
    recordCount: null,
    lastAccessedAt: null,
  },
  {
    repositoryId: 'repo-balances',
    repositoryName: 'BalanceRepository',
    description: 'Stores RTWOWalletBalance records. No fabricated balances.',
    accessStatus: 'not_configured',
    recordCount: null,
    lastAccessedAt: null,
  },
  {
    repositoryId: 'repo-transaction-requests',
    repositoryName: 'TransactionRequestRepository',
    description: 'Stores RTWOTransactionRequest records. No fabricated transactions.',
    accessStatus: 'not_configured',
    recordCount: null,
    lastAccessedAt: null,
  },
  {
    repositoryId: 'repo-assets',
    repositoryName: 'AssetRepository',
    description: 'Stores RTWOAsset registry entries.',
    accessStatus: 'not_configured',
    recordCount: null,
    lastAccessedAt: null,
  },
  {
    repositoryId: 'repo-policies',
    repositoryName: 'PolicyRepository',
    description: 'Stores RTWOWalletPolicy definitions.',
    accessStatus: 'not_configured',
    recordCount: null,
    lastAccessedAt: null,
  },
  {
    repositoryId: 'repo-registry',
    repositoryName: 'WalletRegistryRepository',
    description: 'Stores wallet registry entries.',
    accessStatus: 'not_configured',
    recordCount: null,
    lastAccessedAt: null,
  },
  {
    repositoryId: 'repo-event-history',
    repositoryName: 'WalletEventHistoryRepository',
    description: 'Stores wallet engine event history.',
    accessStatus: 'not_configured',
    recordCount: null,
    lastAccessedAt: null,
  },
];

export type WalletConfigurationBindingStatus = 'not_bound' | 'bound' | 'active' | 'invalid';

export interface RTWOWalletConfigurationBinding {
  bindingId: string;
  configurationId: string;
  configurationName: string;
  providerType: string;
  scope: string;
  status: WalletConfigurationBindingStatus;
  boundAt: WalletTimestamp;
}

export const WALLET_CONFIGURATION_BINDINGS: Omit<
  RTWOWalletConfigurationBinding,
  'bindingId' | 'boundAt'
>[] = [
  {
    configurationId: 'svc-wallet',
    configurationName: 'Wallet Provider Type',
    providerType: 'not_configured',
    scope: 'wallet-engine',
    status: 'not_bound',
  },
  {
    configurationId: 'cfg-005',
    configurationName: 'Cryptography Key Provider',
    providerType: 'not_configured',
    scope: 'wallet-engine.cryptography',
    status: 'not_bound',
  },
  {
    configurationId: 'cfg-002',
    configurationName: 'Ledger Storage Backend',
    providerType: 'not_configured',
    scope: 'wallet-engine.ledger',
    status: 'not_bound',
  },
  {
    configurationId: 'cfg-004',
    configurationName: 'RPC Gateway Endpoint',
    providerType: 'not_configured',
    scope: 'wallet-engine.rpc',
    status: 'not_bound',
  },
  {
    configurationId: 'cfg-003',
    configurationName: 'Storage Backend Provider',
    providerType: 'not_configured',
    scope: 'wallet-engine.storage',
    status: 'not_bound',
  },
];

export interface Phase24ChecklistItem {
  id: string;
  label: string;
  status: 'ready' | 'pending' | 'blocked';
  note: string;
}

export const PHASE24_WALLET_PROVIDER_CHECKLIST: Phase24ChecklistItem[] = [
  {
    id: 'custodial-scaffold',
    label: 'Custodial wallet provider scaffold implemented',
    status: 'ready',
    note: 'Registered — Cryptography, Ledger, RPC, and Storage not connected',
  },
  {
    id: 'non-custodial-scaffold',
    label: 'Non-custodial wallet provider operational',
    status: 'ready',
    note: 'Runtime activated via Wallet Cryptography Bootstrap — explicit wallet creation only',
  },
  {
    id: 'enterprise-scaffold',
    label: 'Enterprise wallet provider scaffold implemented',
    status: 'ready',
    note: 'Registered — backend dependencies not connected',
  },
  {
    id: 'hardware-scaffold',
    label: 'Hardware wallet provider scaffold implemented',
    status: 'ready',
    note: 'Registered — hardware device connection not implemented',
  },
  {
    id: 'provider-registration',
    label: 'Provider registration and catalog bootstrap',
    status: 'ready',
    note: 'Four providers registered via catalog on engine initialization',
  },
  {
    id: 'configuration-binding',
    label: 'Configuration binding service',
    status: 'ready',
    note: 'Binds svc-wallet and dependency configs to wallet provider selection',
  },
  {
    id: 'repository-bootstrap',
    label: 'Repository bootstrap service',
    status: 'ready',
    note: 'Bootstraps eight repositories when provider is ready',
  },
  {
    id: 'real-wallet-ops',
    label: 'Real wallet operations',
    status: 'ready',
    note: 'Address derivation and signing routed through provider abstraction at runtime',
  },
  {
    id: 'runtime-crypto-bridge',
    label: 'Wallet cryptography runtime bridge',
    status: 'ready',
    note: 'CryptographyEngineDelegate registered and providers activated via adapter bootstrap',
  },
  {
    id: 'ledger-balance-ops',
    label: 'Ledger balance and transaction submission',
    status: 'blocked',
    note: 'Balances and ledger-certified transactions require Ledger Engine connection',
  },
];
